/**
 * Synth VR's split and snip (SRVR-0361, SRVR-0362): the desktop editor's `Track.SplitRailAtNode` and
 * `Track.SnipRail`. Both work on the rail of the current type passing through the playhead, never on a
 * selected node; with no node on the playhead one is first put on the rail's in-game line
 * (`RailEditor.AddNodeToActiveRailOnExistingLine` → `Track.GetSnapToRailPosition`). Pure functions; the
 * store resolves the type and the playhead and writes the result as one undo entry.
 */
import type { Note } from '../types/beatmap';
import { noteFilterKind, type NoteFilterKind } from './areaSelection';
import { smoothLine, type Point3 } from '../components/three/gameRailLine';
import { headNode, isRail, nodeMs, sortedNodes, type JoinResult, type Node3 } from './railJoin';

export const SPLIT_ON_NOTE = "Can't split a rail where note of same type exists.";
export const SPLIT_ON_HEAD = "Can't split a rail at its head note.";
export const SPLIT_ON_LAST_NODE = "Can't split a rail at its last node.";

/**
 * `GetSnapToRailPosition`: the in-game line's X/Y at `z`, linearly interpolated between the line points
 * either side of it. The points are the game's (`LineSmoother.SmoothLine`, SRVR-0356) over the head and
 * the nodes in file units.
 */
export function inGameLineXY(points: readonly Point3[], z: number): [number, number] {
  const next = points.findIndex(p => p[2] > z);
  if (next <= 0) {
    const p = next === 0 ? points[0] : points[points.length - 1];
    return [p[0], p[1]];
  }
  const a = points[next - 1];
  const b = points[next];
  const f = (z - a[2]) / (b[2] - a[2]);
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

/** Split (or, with `snip`, cut short) the rail of `kind` through the playhead. */
export function splitAtPlayhead(
  notes: readonly Note[], playheadMs: number, tolMs: number, kind: NoteFilterKind, bpm: number, snip: boolean,
): JoinResult {
  const on = (ms: number) => Math.abs(ms - playheadMs) < tolMs;
  const ofKind = (n: Note) => noteFilterKind(n) === kind;

  // A note of the type on the playhead comes first, whether or not a rail is there.
  if (notes.some(n => ofKind(n) && !isRail(n) && on(n.time_ms))) return { refused: SPLIT_ON_NOTE };

  // `IsNotePosConcurrentWithRailByType`: a rail of the type starts before the playhead and runs to it.
  const through = notes.findIndex(n => ofKind(n) && isRail(n) && n.time_ms < playheadMs && !on(n.time_ms)
    && nodeMs(sortedNodes(n)[n.rail_nodes!.length - 1]) > playheadMs - tolMs);
  if (through < 0) return { refused: null };

  // The rails of the type with a head or node on the playhead, in chart (time) order: the first head or
  // last node met refuses, as the desktop's walk over its segments does.
  let rail = -1;
  let nodes: Node3[] = [];
  let at = -1;
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (!ofKind(n) || !isRail(n)) continue;
    if (on(n.time_ms)) return { refused: SPLIT_ON_HEAD };
    const ns = sortedNodes(n);
    const k = ns.findIndex(p => on(nodeMs(p)));
    if (k < 0) continue;
    if (k === ns.length - 1) return { refused: SPLIT_ON_LAST_NODE };
    if (rail < 0) { rail = i; nodes = ns; at = k; }
  }

  if (rail < 0) {
    // No node on the playhead: put one on the rail's in-game line there, without changing its shape.
    rail = through;
    nodes = sortedNodes(notes[rail]);
    const z = playheadMs * 0.02;
    const [x, y] = inGameLineXY(smoothLine([headNode(notes[rail]), ...nodes]), z);
    at = nodes.findIndex(p => p[2] > z);
    nodes.splice(at, 0, [x, y, z]);
  }

  const orig = notes[rail];
  const out = notes.slice();
  out[rail] = { ...orig, has_rail: true, rail_nodes: nodes.slice(0, at + 1) };
  if (!snip) {
    const [x, y, z] = nodes[at];
    const ms = z / 0.02;
    out.push({
      ...orig,
      time_ms: ms, time_seconds: ms / 1000, time_beats: (ms / 1000) * (bpm / 60),
      position: [x, y, z],
      has_rail: true, rail_nodes: nodes.slice(at + 1),
      note_id: '',
    });
  }
  return { notes: out.sort((a, b) => a.time_ms - b.time_ms) };
}
